import { FLEET_AVAILABILITY_GROUPS, type FleetAvailabilityTruck } from '@numeo/types-main-web-app'
import type { FleetGroups, FleetLoad, FleetTruck } from '../../api/assignDriverApi'

/** How long a row keeps its Undo after an assign. */
export const UNDO_WINDOW_MS = 30_000

/** Candidates shown before "Show all". */
export const CANDIDATES_SHOWN = 5

/** The label a truck with no position shows. */
const NO_LOCATION = 'No location'

const LOCATION_WORDS: Record<FleetAvailabilityTruck['location']['source'], string> = {
    gps: 'GPS',
    last_delivery: 'last delivery',
    unknown: 'location unknown',
}

/** One truck the picker lists, read from the server's candidates. */
export interface AssignCandidate {
    truckId: string
    /** The truck number, as the card names it ("102"). */
    truckLabel: string
    /** Driver names; a team lists both. Empty when the truck has no driver. */
    drivers: string[]
    noDriver: boolean
    /** A driver on the truck is inactive: the truck is ranked last and shows its badge. */
    inactive: boolean
    /** When the truck is free (ISO), or null when unknown. */
    readyAt: string | null
    /** False when the truck is not free before the pickup (shown in warning). Undefined when the answer did not say. */
    readyBeforePickup?: boolean
    /** Road miles from the truck's expected position to the pickup, or null when not known. */
    deadheadMiles: number | null
    /** The position's label, or "No location" when the truck has none. */
    location: string
    /** The words for the position's source, shown in its tooltip. */
    locationSource: string
    locationMuted: boolean
    /** The answer placed the truck (it has coordinates). */
    hasCoords: boolean
    /** The truck's home time, when its driver is off: the status name and when it ends (ISO). */
    off?: { statusName: string; endsAt?: string }
}

export const hasInactiveDriver = (truck: FleetTruck): boolean => truck.drivers.some((driver) => driver.inactive === true)

/** The groups as the picker and the Who's free view draw them: a truck with an inactive driver is home time, ranked last there. */
export function fleetBucketsOf(groups: FleetGroups): FleetGroups {
    const out: FleetGroups = { ready_now: [], ready_later: [], on_load: [], off: [], out_of_service: [] }
    for (const group of FLEET_AVAILABILITY_GROUPS) for (const truck of groups[group] ?? []) out[hasInactiveDriver(truck) ? 'off' : group].push(truck)
    out.off.sort((a, b) => Number(hasInactiveDriver(a)) - Number(hasInactiveDriver(b)))
    return out
}

/** A truck's position: its label, else "No location" (muted), with its source's words for the tooltip. */
export const locationOf = (truck: FleetTruck): { label: string; source: string; muted: boolean; hasCoords: boolean } => ({
    label: truck.location.label ?? (truck.location.source === 'unknown' ? NO_LOCATION : LOCATION_WORDS[truck.location.source]),
    source: LOCATION_WORDS[truck.location.source],
    muted: truck.location.source === 'unknown' && !truck.location.label,
    hasCoords: typeof truck.location.lat === 'number',
})

/** The picker's rows, in the server's order (home time last); an inactive driver's truck goes after every other. */
export function candidatesOf(trucks: FleetTruck[]): AssignCandidate[] {
    return trucks
        .map((truck): AssignCandidate => {
            const offDriver = truck.drivers.find((driver) => driver.off)?.off
            const position = locationOf(truck)
            return {
                truckId: truck.truck_id,
                truckLabel: truck.truck_number,
                drivers: truck.drivers.map((driver) => driver.name),
                noDriver: truck.no_driver || truck.drivers.length === 0,
                inactive: hasInactiveDriver(truck),
                readyAt: truck.ready_at,
                readyBeforePickup: truck.ready_before_pickup,
                deadheadMiles: typeof truck.deadhead_miles === 'number' ? truck.deadhead_miles : null,
                location: position.label,
                locationSource: position.source,
                locationMuted: position.muted,
                hasCoords: position.hasCoords,
                ...(truck.group === 'off' && offDriver ? { off: { statusName: offDriver.status_name, endsAt: offDriver.ends_at } } : {}),
            }
        })
        .sort((a, b) => Number(a.inactive) - Number(b.inactive))
}

/** The rows the picker draws: the first few, or all of them when "Show all" is on. */
export const visibleCandidates = (candidates: AssignCandidate[], showAll: boolean): AssignCandidate[] =>
    showAll ? candidates : candidates.slice(0, CANDIDATES_SHOWN)

/** A clock time in the browser's zone, "18:30". Empty when the time is not a date. */
export const clockOf = (iso: string): string => {
    const at = new Date(iso)
    return Number.isNaN(at.getTime()) ? '' : at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
}

/** A zone the browser knows, else undefined (the browser's own zone). A zone the answer names wrongly must not throw. */
const knownZone = (timeZone: string | undefined): string | undefined => {
    if (!timeZone) return undefined
    try {
        Intl.DateTimeFormat('en-US', { timeZone })
        return timeZone
    } catch {
        return undefined
    }
}

/** A day, "Nov 1", in the answer's `timeZone` (the company's zone), else the browser's. Empty when the time is not a date. */
const dayWordsOf = (iso: string | null | undefined, timeZone?: string): string => {
    if (!iso) return ''
    const at = new Date(iso)
    return Number.isNaN(at.getTime()) ? '' : at.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: knownZone(timeZone) })
}

/** The time a truck is free, in the browser's zone, as the picker shows it: "Ready 06:30". Null when unknown. */
export const readyWordsOf = (readyAt: string | null, now: Date = new Date()): string | null => {
    if (!readyAt) return null
    const at = new Date(readyAt)
    if (Number.isNaN(at.getTime())) return null
    return at.getTime() <= now.getTime() ? 'Ready now' : `Ready ${clockOf(readyAt)}`
}

/** A home-time row's line: "Home time until 12:00", with the status name when it is not plain home time. */
export const homeTimeWordsOf = (off: NonNullable<AssignCandidate['off']>): string => {
    const until = off.endsAt ? ` until ${clockOf(off.endsAt)}` : ''
    const plain = off.statusName.trim().toLowerCase() === 'home time'
    return `Home time${until}${plain ? '' : ` · ${off.statusName}`}`
}

/** Road miles, as the picker shows a deadhead: "14 mi". An unknown distance is "— mi". */
export const milesWords = (miles: number | null | undefined): string => (typeof miles === 'number' ? `${Math.round(miles)} mi` : '— mi')

/** The pickup's date and time as the picker shows them: "Nov 1, 08:00". Empty when unknown. */
const pickupWhenOf = (iso: string | null | undefined): string => {
    const day = dayWordsOf(iso)
    const clock = iso ? clockOf(iso) : ''
    return day && clock ? `${day}, ${clock}` : day || clock
}

/** The picker's header: "Pick a truck for #2334765 · pickup Westfield, ME · Nov 1, 08:00", a missing part left out. */
export const pickupHeaderOf = (load: FleetLoad): string =>
    [
        load.load_number ? `Pick a truck for #${load.load_number}` : 'Pick a truck',
        load.pickup_place ? `pickup ${load.pickup_place}` : '',
        pickupWhenOf(load.pickup_at),
    ]
        .filter(Boolean)
        .join(' · ')

/** A load's overdue line: "Pickup passed Nov 1", the day in the company's zone. */
export const pickupPassedWordsOf = (iso: string | null | undefined, timeZone?: string): string => {
    const day = dayWordsOf(iso, timeZone)
    return day ? `Pickup passed ${day}` : 'Pickup passed'
}

/** A load's overdue line: "Delivery date passed Nov 1", the day in the company's zone. */
export const deliveryPassedWordsOf = (iso: string | null | undefined, timeZone?: string): string => {
    const day = dayWordsOf(iso, timeZone)
    return day ? `Delivery date passed ${day}` : 'Delivery date passed'
}

/** A load whose delivery date has passed: the picker opens with the choices, and the date is in the company's zone. */
export interface OverdueDelivery {
    at: string | null
    timezone?: string
}

/** The two answers a load with a passed delivery date takes, as the card's confirm values, with the words the picker shows. */
export const OVERDUE_CHOICES = [
    { confirm: 'keep_overdue_open', label: 'Assign anyway and keep it open' },
    { confirm: 'complete_overdue', label: 'Mark it completed' },
] as const

export type OverdueConfirm = (typeof OVERDUE_CHOICES)[number]['confirm']
